import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaLock, FaUser, FaEye, FaEyeSlash, FaHeart, FaSpinner } from 'react-icons/fa';
import axios from 'axios';
import LanguageSwitcher from '../components/LanguageSwitcher';

const isDevelopment = process.env.NODE_ENV === 'development';
const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '',
  timeout: 30000,
  withCredentials: true // Important for cookies
});

function GalleryLogin() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  // Determine if this is for mobile or desktop
  const isMobile = location.pathname.includes('mobile');
  const targetPath = isMobile ? '/camera-roll-mobile' : '/camera-roll';

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axiosInstance.get('/api/auth/gallery-verify');
        if (response.data.success) {
          // Already authenticated, redirect to gallery
          navigate(targetPath, { replace: true });
        }
      } catch (error) {
        // Not authenticated, stay on login page
      } finally {
        setIsCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate, targetPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axiosInstance.post('/api/auth/gallery-login', {
        username: username.trim(),
        password
      });

      if (response.data.success) {
        // Redirect to gallery
        navigate(targetPath, { replace: true });
      }
    } catch (error) {
      if (error.response?.status === 401) {
        setError(t('gallery.login.invalidCredentials'));
      } else {
        setError(t('gallery.login.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-white to-rose-50">
        <div className="flex flex-col items-center gap-4">
          <FaSpinner className="animate-spin text-4xl text-pink-500" />
          <p className="text-gray-500">{t('gallery.login.checking')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-pink-50 via-white to-rose-50 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-pink-200/30 to-transparent rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-rose-200/30 to-transparent rounded-full translate-x-1/2 translate-y-1/2 blur-3xl" />
      <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-purple-100/20 to-transparent rounded-full blur-2xl" />
      
      {/* Header */}
      <header className="relative z-10 p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img 
            src="/new-logo.png" 
            alt="Weddingram Logo" 
            className="h-10 w-auto"
          />
          <h1 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent hidden sm:block">
            weddingram
          </h1>
        </div>
        <LanguageSwitcher />
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl shadow-pink-500/10 border border-white/50 p-8 sm:p-10">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">
                weddingram
              </h1>
            </div>

            {/* Title */}
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">
                {t('gallery.login.title')}
              </h2>
              <p className="text-gray-500 text-sm">
                {t('gallery.login.subtitle')}
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaUser className="text-gray-400" />
                </div>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder={t('gallery.login.username')}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500 transition-all duration-200 text-gray-800 placeholder-gray-400"
                  required
                  disabled={isLoading}
                />
              </div>

              {/* Password Field */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaLock className="text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('gallery.login.password')}
                  className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/50 focus:border-pink-500 transition-all duration-200 text-gray-800 placeholder-gray-400"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm text-center animate-shake">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !username.trim() || !password}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 text-white font-semibold rounded-xl shadow-lg shadow-pink-500/30 hover:shadow-xl hover:shadow-pink-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t('gallery.login.loading')}
                  </>
                ) : (
                  t('gallery.login.submit')
                )}
              </button>
            </form>
          </div>

          {/* Footer Text */}
          <p className="text-center text-gray-400 text-sm mt-6">
            {t('gallery.login.footer')}
          </p>
        </div>
      </main>

      {/* Bottom Decorative Hearts */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 opacity-20">
        <FaHeart className="text-pink-400 text-sm animate-pulse" style={{ animationDelay: '0s' }} />
        <FaHeart className="text-rose-400 text-xs animate-pulse" style={{ animationDelay: '0.2s' }} />
        <FaHeart className="text-pink-500 text-sm animate-pulse" style={{ animationDelay: '0.4s' }} />
      </div>
    </div>
  );
}

export default GalleryLogin;
